import { useEffect, useRef, useState } from "react";

interface StatsCounterProps {
  value: number;
  currency?: string;
  duration?: number;
  suffix?: string;
  formatValue?: (value: number) => string;
}

export function StatsCounter({ value, currency, duration = 1.2, suffix = "", formatValue }: StatsCounterProps) {
  const counterRef = useRef<HTMLSpanElement>(null);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const element = counterRef.current;
    if (!element || !Number.isFinite(value)) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = 0;
    let observer: IntersectionObserver | undefined;
    setDisplayValue(0);

    const animate = () => {
      if (reducedMotion || duration <= 0) {
        setDisplayValue(value);
        return;
      }

      const startedAt = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - startedAt) / (duration * 1000), 1);
        setDisplayValue(value * (1 - (1 - progress) ** 3));
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          observer?.disconnect();
          animate();
        }
      }, { threshold: 0.25 });
      observer.observe(element);
    } else {
      animate();
    }

    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [duration, value]);

  const formatted = formatValue
    ? formatValue(displayValue)
    : currency
      ? new Intl.NumberFormat("en-CA", { currency, maximumFractionDigits: 1, notation: "compact", style: "currency" }).format(displayValue)
      : new Intl.NumberFormat("en-CA", { maximumFractionDigits: 1, notation: "compact" }).format(displayValue);

  return <span className="stats-counter" ref={counterRef}>{formatted}{suffix}</span>;
}
